import { Auth, onAuthStateChanged } from '@angular/fire/auth';
import { Store } from '@ngrx/store';
import { firstValueFrom, timeout } from 'rxjs';
import { filter, take } from 'rxjs/operators';
import { AppState } from '../store/app.state';
import * as AuthActions from '../store/auth/auth.actions';
import { selectLoading } from '../store/auth/auth.selectors';
import { InviteFlowService } from '../services/invite-flow.service';

const AUTH_INIT_TIMEOUT_MS = 4000;
const LOAD_USER_TIMEOUT_MS = 5000;

/**
 * Inizializza l'auth prima che l'app e il router partano.
 * Attende che Firebase emetta lo stato (o un timeout); se c'è un utente
 * carica l'utente dal backend. Se firebaseUser è null non si fa loadUser
 * per evitare 401 dopo logout (race con token in localStorage).
 */
export function authInitializer(
  auth: Auth,
  store: Store<AppState>,
  inviteFlowService: InviteFlowService
): () => Promise<void> {
  return () =>
    new Promise<void>((resolve) => {
      let resolved = false;
      let firebaseHasFired = false;
      const tryResolve = () => {
        if (resolved) return;
        resolved = true;
        resolve();
      };

      const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
        firebaseHasFired = true;
        // Se è in corso l'accettazione di un invito, non fare loadUser
        if (inviteFlowService.isInProgress()) {
          tryResolve();
          return;
        }
        try {
          if (firebaseUser) {
            const token = await firebaseUser.getIdToken();
            localStorage.setItem('firebaseToken', token);
            store.dispatch(AuthActions.loadUser());
            await firstValueFrom(
              store.select(selectLoading).pipe(
                filter((loading) => !loading),
                take(1),
                timeout(LOAD_USER_TIMEOUT_MS)
              )
            ).catch(() => undefined);
          }
          // Quando firebaseUser è null (logout o non loggato) non fare loadUser():
          // eviterebbe 401 perché dopo logout il token può essere ancora in localStorage
          // per un attimo (race) e si finirebbe a chiamare l'API con token poi invalidato.
        } catch {
          // ignore
        } finally {
          tryResolve();
        }
      });

      // Se Firebase non emette entro il timeout (es. rete lenta), sblocca comunque l'app
      setTimeout(() => {
        if (!firebaseHasFired) {
          unsubscribe();
          tryResolve();
        }
      }, AUTH_INIT_TIMEOUT_MS);
    });
}
